import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useQrOrders } from '../context/QrOrdersContext';
import SearchSelect from './SearchSelect';
import { fmtTime } from '../utils/dateFormat';

const formatMoney = (v) => `${Number(v || 0).toFixed(2)} ₼`;

export default function QrOrdersModal() {
    const { qrRequests, qrCount, modalOpen, closeModal, loadQrRequests } = useQrOrders();
    const navigate = useNavigate();

    const [tables, setTables] = useState([]);
    const [qrAccepting, setQrAccepting] = useState(null);
    const [qrTableSelect, setQrTableSelect] = useState({});

    // Fetch tables whenever modal opens
    useEffect(() => {
        if (!modalOpen) return;
        api.get('/tables')
            .then(res => setTables(res.data?.data || []))
            .catch(() => setTables([]));
    }, [modalOpen]);

    const handleAcceptQr = async (req) => {
        const tableId = qrTableSelect[req.id];
        if (!tableId) { toast.error('Masa seçin.'); return; }
        setQrAccepting(req.id);
        try {
            const res = await api.post(`/qr-orders/${req.id}/accept`, { table_id: Number(tableId) });
            toast.success(res.data?.msg || 'QR sifariş qəbul edildi.');
            await loadQrRequests();
            setQrTableSelect(prev => { const n = { ...prev }; delete n[req.id]; return n; });
            closeModal();
            if (res.data?.order_id) navigate(`/waiter/order/${res.data.order_id}`);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Sifariş qəbul edilmədi.');
        } finally {
            setQrAccepting(null);
        }
    };

    const handleRejectQr = async (req) => {
        setQrAccepting(req.id);
        try {
            await api.post(`/qr-orders/${req.id}/reject`);
            toast.success('Sifariş rədd edildi.');
            await loadQrRequests();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Əməliyyat baş tutmadı.');
        } finally {
            setQrAccepting(null);
        }
    };

    if (!modalOpen) return null;

    const emptyTables = tables.filter(
        t => t.active !== 0 && !t.order_id && (t.occupancy_status || 'bos') === 'bos'
    );

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
            onClick={e => e.target === e.currentTarget && closeModal()}
        >
            <div className="max-h-[90vh] w-full overflow-hidden rounded-t-[28px] bg-white shadow-xl sm:max-w-lg sm:rounded-2xl">
                {/* Header */}
                <div className="border-b border-gray-100 px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">📲 QR Sifarişlər</h3>
                            <p className="mt-0.5 text-xs text-gray-400">
                                {qrCount === 0 ? 'Gözləyən sifariş yoxdur' : `${qrCount} gözləyən sifariş`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={loadQrRequests}
                                className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-200"
                            >
                                🔄
                            </button>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Bağla
                            </button>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="overflow-y-auto px-5 py-4 space-y-4" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                    {qrRequests.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-12 text-center text-gray-400">
                            <span className="text-4xl">📋</span>
                            <p className="text-sm">Hazırda gözləyən QR sifariş yoxdur.</p>
                        </div>
                    ) : (
                        qrRequests.map(req => {
                            const items = Array.isArray(req.items) ? req.items : [];
                            const isProcessing = qrAccepting === req.id;
                            const selectedTableId = qrTableSelect[req.id] || '';

                            return (
                                <div key={req.id} className="rounded-2xl border border-orange-200 bg-orange-50/40 overflow-hidden">
                                    {/* Request header */}
                                    <div className="border-b border-orange-100 bg-orange-50 px-4 py-3 flex items-center justify-between gap-2">
                                        <div>
                                            <span className="text-xs font-bold text-orange-600 uppercase tracking-wide">
                                                QR Sifariş #{req.id}
                                            </span>
                                            <div className="text-[11px] text-orange-400 mt-0.5">
                                                {fmtTime(req.created_at)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Items */}
                                    <div className="px-4 pt-3 space-y-1.5">
                                        {items.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                                                <span className="font-semibold text-gray-800">
                                                    {item.qty}× {item.name || `Məhsul #${item.id}`}
                                                    {item.is_set && (
                                                        <span className="ml-1 text-xs font-normal text-orange-500">Set</span>
                                                    )}
                                                    {item._variantName && (
                                                        <span className="ml-1 text-xs font-normal text-gray-500">({item._variantName})</span>
                                                    )}
                                                </span>
                                                <span className="flex-shrink-0 font-bold text-gray-700">
                                                    {formatMoney(Number(item.qty || 0) * Number(item.price || 0))}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Customer note */}
                                    {req.customer_note && (
                                        <div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                            💬 {req.customer_note}
                                        </div>
                                    )}

                                    {/* Table select + actions */}
                                    <div className="px-4 py-3 mt-2 flex flex-wrap gap-2 items-center">
                                        <div className="flex-1 min-w-[140px]">
                                            <SearchSelect
                                                value={selectedTableId}
                                                onChange={v => setQrTableSelect(prev => ({ ...prev, [req.id]: v }))}
                                                options={emptyTables.map(t => ({
                                                    value: t.id,
                                                    label: t.name + (t.room_name ? ` (${t.room_name})` : '')
                                                }))}
                                                placeholder={emptyTables.length ? 'Masa seçin...' : 'Boş masa yoxdur'}
                                                disabled={isProcessing}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleAcceptQr(req)}
                                            disabled={isProcessing || !selectedTableId}
                                            className="rounded-xl bg-green-500 px-4 py-2 text-sm font-bold text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isProcessing ? '...' : '✓ Qəbul et'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRejectQr(req)}
                                            disabled={isProcessing}
                                            className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-100 disabled:opacity-50"
                                        >
                                            ✕ Rədd et
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
